import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { isKarkhanaSupervisor } from "../../../../helpers/karkhanaEnabled";
import { closeTransferDispute, getTransfer } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { fmtDay, rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard, { readRole } from "../CashbookGuard";
import { fmtDateTime, refusalText } from "../money";
import { myUserId, ownerStoryLine } from "../owner/ownerText";
import type { TransferDetailResponse } from "../types";
import { afterRejectText, GiverAfterReject, IncomingCard, rejectNoteText, waitingOnGiver } from "./TransferCards";
import "../cashbook.scss";
import "../owner/owner.scss";
import "./transfer.scss";

/** Who is looking: the receiver answers, the giver corrects, staff read the story and close disputes. */
export const viewerOf = (d: Pick<TransferDetailResponse, "transfer">, me: string, supervisor: boolean): "RECEIVER" | "GIVER" | "STAFF" | "OTHER" => {
  if (!supervisor) return "STAFF";
  if (d.transfer.to?.userId === me) return "RECEIVER";
  if (d.transfer.from?.userId === me) return "GIVER";
  return "OTHER";
};

const DetailScreen = () => {
  const { t } = useKT();
  const { id } = useParams<{ id: string }>();
  const status = useCashbookStatus();
  const supervisor = isKarkhanaSupervisor(readRole());
  const [d, setD] = useState<TransferDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getTransfer(id);
      if (res?.success && res.data) setD(res.data);
      else setError(refusalText(res, t));
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    status.refresh();
  };

  const header = <PageHeader title={t("Handover")} back={kp.cashBook()} backLabel={t("Back")} />;

  if (error && !d)
    return (
      <div className="cb">
        {header}
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!d)
    return (
      <div className="cb" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
      </div>
    );

  const x = d.transfer;
  const who = viewerOf(d, myUserId(), supervisor);
  const closed = d.story.some((s) => s.action === "TRANSFER_DISPUTE_CLOSED");

  const closeDispute = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setCloseError(null);
    try {
      const res = await closeTransferDispute(x.id, { note: note.trim() });
      if (!res?.success) {
        setCloseError(refusalText(res, t));
        return;
      }
      setNote("");
      changed();
    } catch {
      setCloseError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(false);
    }
  };

  const stateLine =
    x.state === "CONFIRMED"
      ? t("{{name}} said received · {{when}}", { name: x.to.name, when: fmtDateTime(x.decidedAt) })
      : x.state === "REJECTED"
        ? t('{{name}} said no: "{{note}}"', { name: x.to.name, note: rejectNoteText(x, t) })
        : t('Waiting for {{name}} to tap "Received" · {{amount}} not counted for anyone yet', { name: x.to.name, amount: rupees(x.amount) });

  return (
    <div className={`cb${who === "STAFF" ? " cbo" : ""}`} style={who === "STAFF" ? { maxWidth: 760 } : undefined}>
      {header}
      <div className="cb-big">
        <div className="cb-big__value" aria-live="polite">
          {rupees(x.amount)}
        </div>
        <div className="cb-big__sum">
          {x.from.name} → {x.to.name} · {fmtDay(x.date)}
          {x.note ? ` · "${x.note}"` : ""}
        </div>
      </div>

      {who === "RECEIVER" && x.state === "IN_TRANSIT" ? <IncomingCard x={x} onChanged={changed} /> : null}
      {who === "RECEIVER" && x.state === "REJECTED" ? (
        <div className="cb-card is-warn" role="status">
          <p>{afterRejectText(x, t)}</p>
        </div>
      ) : null}
      {who === "GIVER" && waitingOnGiver(x) ? <GiverAfterReject x={x} onChanged={changed} /> : null}
      {!(who === "RECEIVER" && x.state !== "CONFIRMED") && !(who === "GIVER" && waitingOnGiver(x)) ? (
        <div className={`cb-card${x.state === "CONFIRMED" ? " is-ok" : x.state === "REJECTED" ? " is-warn" : ""}`}>
          <p>{stateLine}</p>
        </div>
      ) : null}

      {d.revision ? (
        <Link to={kp.cashTransfer(d.revision.id)} className="cb-card cb-transfer is-link">
          <p>{t("Given again: {{amount}}", { amount: rupees(d.revision.amount) })} ›</p>
        </Link>
      ) : null}
      {x.revisesId ? (
        <Link to={kp.cashTransfer(x.revisesId)} className="cb-card cb-transfer is-link">
          <p>{t("This corrects an earlier handover")} ›</p>
        </Link>
      ) : null}

      {who === "STAFF" ? (
        <>
          <h3 className="cb-section">{t("What happened")}</h3>
          <ol className="cbo-story">
            {d.story
              .map((s) => ({ s, line: ownerStoryLine(s, t) }))
              .filter((z) => z.line)
              .map(({ s, line }) => (
                <li key={s.seq}>{line}</li>
              ))}
          </ol>
          {x.disputed && !closed && !x.revisedById ? (
            <div className="cbo-decide">
              <p>
                <strong>{t("{{from}} says they gave {{to}} {{amount}} (handover disputed).", { from: x.from.name, to: x.to.name, amount: rupees(x.amount) })}</strong>
              </p>
              {x.disputeNote ? <p className="cb-muted">"{x.disputeNote}"</p> : null}
              <label className="cb-field">
                <span className="cb-field__label">{t("What did you find?")}</span>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("e.g. Talked to both; Suresh found ₹500")} />
              </label>
              <div className="cbo-decide__btns">
                <button type="button" className="kh-btn is-primary" disabled={!note.trim() || busy} onClick={closeDispute}>
                  {busy ? t("Saving…") : t("Close dispute")}
                </button>
              </div>
              {closeError ? (
                <p className="cbo-row__err" role="alert">
                  {closeError}
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/transfer/:id — receiver, giver or staff (V1_SPEC 11.5 B5, B6, owner view). */
const TransferDetail = () => (
  <CashbookGuard>
    <DetailScreen />
  </CashbookGuard>
);

export default TransferDetail;
